import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Nullable } from '@types'

import { POneEvent } from '@modules/auth/hooks/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { preparePendoImportProspectsEvent } from '@modules/pendo'
import { usePendoTrackEvent } from '@modules/pendo/hooks/usePendoTrackEvent'
import { PendoEventType, PendoImportProspectsEventType } from '@modules/pendo/types'
import { useShortlists } from '@modules/shortlist/hooks/useShortlistMap'
import { Shortlist } from '@modules/shortlist/types'
import { Box, Dialog } from '@mui/material'
import useSelection from '@shared/hooks/useSelection'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import shortlistApi from '../../api'
import EventSelectionView from './EventSelectionView'
import ProspectsSelectionView from './ProspectsSelectionView'
import ShortlistSelectionView from './ShortlistSelectionView'
import { getFilters } from './constant'
import { ImportProspectsRequestTypes, ImportProspectsStatus, ImportProspectsView } from './types'
import { usePreselectFilters } from './usePreselectFilters'

interface ImportProspectsPopupProps {
  isVisible: boolean
  setIsVisible: (value: boolean) => void
  events: POneEvent[]
  enablePreselect: boolean
  selectShortlist?: (shId: Shortlist['id']) => void
}

const ImportProspectsPopup = ({
  isVisible,
  selectShortlist,
  setIsVisible,
  events: rawEvents = [],
  enablePreselect,
}: ImportProspectsPopupProps) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()
  const [currentView, setCurrentView] = useState<ImportProspectsView>(ImportProspectsView.event)
  const [selectedEventId, setSelectedEventId] = useState<Nullable<string>>(null)
  const [selectedParticipantId, setSelectedParticipantId] = useState<number>(0)
  const [filtersPreSelected, setFiltersPreselected] = useState<boolean>(false)
  const [selectedFilters, setSelectedFilters] = useState<(number | ImportProspectsRequestTypes | ImportProspectsStatus)[]>([])
  const [allFilterIds, setAllFilterIds] = useState<(number | ImportProspectsRequestTypes | ImportProspectsStatus)[]>([])
  const { user } = useAuthContext()
  const dialogPaperRef = useRef<HTMLDivElement | null>(null)
  const dialogContentRef = useRef<HTMLDivElement | null>(null)

  const { data: tags = [] } = useQuery({
    queryKey: [shortlistApi.getUserTags.key, selectedEventId],
    queryFn: () => shortlistApi.getUserTags.fn({ eventId: selectedEventId as string }),
    placeholderData: keepPreviousData,
    enabled: !!selectedEventId,
  })
  const location = useLocation()
  const { trackPendoEvent } = usePendoTrackEvent()
  const { canAddShortlists, rawShortlists } = useShortlists()

  const { data: participants = [] } = useQuery({
    queryKey: [shortlistApi.getCompanyParticipants.key, selectedEventId],
    queryFn: () => shortlistApi.getCompanyParticipants.fn({ eventId: selectedEventId as string }),
    placeholderData: keepPreviousData,
    enabled: !!selectedEventId,
  })

  useEffect(() => {
    if (!dialogPaperRef.current) {
      return
    }

    dialogPaperRef.current.style.height = 'auto'

    setTimeout(() => {
      if (dialogContentRef.current && dialogPaperRef.current) {
        const { height } = dialogContentRef.current.getBoundingClientRect()
        dialogPaperRef.current.style.height = `${height}px`
      }
    }, 100)
  }, [currentView])

  const filters = useMemo(() => getFilters(tags, t), [tags, t])

  useEffect(() => {
    const allFilters = filters.flatMap(i => i.options.map(option => option.id))
    setAllFilterIds(filters.flatMap(i => i.options.map(option => option.id)))
    if (!filtersPreSelected) {
      setSelectedFilters(allFilters)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedEventId, t])

  const {
    selectedIds: selectedFilterIds,
    toggleSelectItem: toggleFilterSelect,
    clearSelectedItems: clearSelectedFilters,
    toggleSelectItems: toggleFilters,
  } = useSelection(allFilterIds, { initiallySelectedItems: selectedFilters, isReactiveInitiallySelectedItems: true })

  const { data: { total: matchingProspectsCount, result: prospects } = {} } = useQuery({
    queryKey: [
      shortlistApi.getProspectsByFilters.key,
      selectedEventId,
      selectedFilterIds,
      user?.companyId,
      filters,
      selectedParticipantId,
    ],
    queryFn: () =>
      shortlistApi.getProspectsByFilters.fn({
        eventId: selectedEventId as string,
        tagIds: selectedFilterIds.filter(id =>
          filters.some(f => f.key === 'tag' && f.options.some(option => option.id === id)),
        ) as number[],
        requests: selectedFilterIds.filter(id =>
          filters.some(f => f.key === 'request' && f.options.some(option => option.id === id)),
        ) as ImportProspectsRequestTypes[],
        statuses: selectedFilterIds.filter(id =>
          filters.some(f => f.key === 'status' && f.options.some(option => option.id === id)),
        ) as ImportProspectsStatus[],
        companyId: user?.companyId as number,
        participantId: selectedParticipantId,
      }),

    placeholderData: keepPreviousData,
    enabled: filters && !!selectedEventId && currentView !== ImportProspectsView.event && !!selectedFilterIds?.length && !!user,
  })

  const { mutate: submitImport } = useMutation({
    mutationFn: shortlistApi.submitImportingProspects.fn,
    onSuccess: (shId: number) => {
      selectShortlist?.(shId)
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, shId] })
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })

      setSelectedEventId(null)
      clearSelectedFilters()
      setIsVisible(false)
    },
  })

  const sendPendoButtonClickEvent = ({
    eventType,
    prospectId,
    participantId,
    filterNames,
  }: {
    eventType: PendoImportProspectsEventType
    prospectId?: number
    participantId?: number
    filterNames?: string[]
  }) => {
    const data = preparePendoImportProspectsEvent({
      eventType,
      user,
      location: location.pathname,
      eventName: rawEvents.find(ev => Number(ev.id) === Number(selectedEventId))?.name,
      profileId: prospectId,
      participantId,
      filterNames,
    })
    trackPendoEvent(PendoEventType.ImportProspects, data)
  }

  const handleFilterChange = (id: string) => {
    const filter = filters.find(f => f.key === id)
    if (filter?.title) {
      sendPendoButtonClickEvent({ eventType: PendoImportProspectsEventType.FilterSelected, filterNames: [filter.title] })
    }
    toggleFilterSelect(id as unknown as number)
  }

  const handleFiltersChange = (ids: string[]) => {
    const filterNames = filters.filter(f => ids.includes(f.key)).map(f => f.title)
    sendPendoButtonClickEvent({ eventType: PendoImportProspectsEventType.FilterSelected, filterNames })
    toggleFilters(ids as unknown as number[])
  }

  const {
    selectedIds: selectedProspectIds,
    toggleSelectItem: toggleSelectProspect,
    toggleSelectAll: toggleSelectAllProspects,
    toggleSelectItems: toggleProspectsBulk,
    selectAll: selectAllProspects,
  } = useSelection(prospects?.flatMap(i => i.companies.map(c => c.id)).flat() ?? [])

  const toggleUnExisting = () => {
    toggleProspectsBulk(selectedProspectIds.filter(id => !prospects?.some(pr => pr.companies?.some(c => c.id === id))))
  }

  const handleSelectAllProspects = () => {
    sendPendoButtonClickEvent({ eventType: PendoImportProspectsEventType.SelectSpecificSelectAllTickBoxClick })
    selectAllProspects()
  }

  const handleToggleSelectAllProspects = () => {
    sendPendoButtonClickEvent({ eventType: PendoImportProspectsEventType.SelectSpecificSelectAllTickBoxClick })
    toggleSelectAllProspects()
  }

  const handleSelectProspect = (prospectId?: number) => {
    sendPendoButtonClickEvent({ eventType: PendoImportProspectsEventType.SelectSpecificSelectTickBoxClick, prospectId })
    toggleSelectProspect(prospectId)
  }

  const handleConfirm = () => {
    const prospectIdsToSubmit =
      selectedProspectIds.length === 0 ? prospects?.flatMap(i => i.companies.map(c => c.id)).flat() ?? [] : selectedProspectIds

    if (!canAddShortlists && rawShortlists?.length === 1) {
      submitImport({ shId: rawShortlists[0].id, prospectIds: prospectIdsToSubmit })
    } else {
      if (selectedProspectIds.length === 0) {
        selectAllProspects()
      }
      setCurrentView(ImportProspectsView.shortlist)
    }
  }
  useEffect(() => {
    toggleUnExisting()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospects])

  const events = useMemo(
    () =>
      rawEvents
        .slice()
        .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())
        .map(userEvent => ({
          label: userEvent.name,
          value: userEvent.id.toString(),
        })),
    [rawEvents],
  )

  useEffect(() => {
    if (!selectedEventId && events?.length) {
      setSelectedEventId(events[0].value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events])

  const handleEventSelect = (eventId: string) => {
    setSelectedEventId(eventId)
  }

  const handleSelectParticipant = (participantId: number) => {
    preparePendoImportProspectsEvent({ eventType: PendoImportProspectsEventType.ParticipantSelected, participantId })
    setSelectedParticipantId(participantId)
  }

  usePreselectFilters({
    onSelectEvent: eventId => {
      handleEventSelect(eventId)
      setCurrentView(ImportProspectsView.prospect)
    },
    onSelectFilters: filterIds => {
      toggleFilters(filterIds)
      setAllFilterIds(filterIds)
      if (filterIds.length) {
        setSelectedFilters(filterIds)
        setFiltersPreselected(true)
      }
    },
    enablePreselect,
  })

  const renderCurrentView = () => {
    if (currentView === ImportProspectsView.event) {
      return (
        <EventSelectionView
          events={events}
          selectedEventId={selectedEventId}
          onSelectEvent={handleEventSelect}
          onConfirm={() => selectedEventId && setCurrentView(ImportProspectsView.prospect)}
          onCancel={() => {
            setSelectedEventId(null)
            setIsVisible(false)
          }}
        />
      )
    }

    if (currentView === ImportProspectsView.prospect) {
      return (
        <ProspectsSelectionView
          filters={filters}
          participants={participants}
          selectedIds={selectedFilterIds as string[]}
          handleFilterChange={handleFilterChange}
          handleMultipleFiltersChange={handleFiltersChange}
          prospects={prospects}
          selectedProspectIds={selectedProspectIds}
          onProspectSelect={handleSelectProspect}
          onToggleSelectAllProspects={handleToggleSelectAllProspects}
          onSelectAllProspects={handleSelectAllProspects}
          onConfirm={handleConfirm}
          onSendPendoEvent={sendPendoButtonClickEvent}
          onCancel={() => {
            setSelectedEventId(null)
            clearSelectedFilters()
            setIsVisible(false)
            sendPendoButtonClickEvent({ eventType: PendoImportProspectsEventType.MainPopoverCancelClick })
          }}
          matchingProspectsCount={!selectedFilterIds?.length ? 0 : matchingProspectsCount}
          onSelectParticipant={handleSelectParticipant}
          selectedParticipantId={selectedParticipantId}
        />
      )
    }

    return (
      <ShortlistSelectionView
        onCancel={() => {
          setSelectedEventId(null)
          clearSelectedFilters()
          setIsVisible(false)

          return true
        }}
        goBack={() => setCurrentView(ImportProspectsView.prospect)}
        onSubmit={(shId: Shortlist['id']) => submitImport({ shId, prospectIds: selectedProspectIds })}
      />
    )
  }

  return (
    <Dialog
      open={isVisible}
      onClose={(e, reason) => {
        if (reason === 'backdropClick') {
          if (currentView === ImportProspectsView.event) {
            setIsVisible(false)
          }
        }
      }}
      PaperProps={{
        ref: dialogPaperRef,
      }}
    >
      <Box
        ref={dialogContentRef}
        data-testid="import-prospects-dialog-wrapper"
        p="16px"
        sx={{ maxHeight: '660px', overflow: 'hidden' }}
      >
        {renderCurrentView()}
      </Box>
    </Dialog>
  )
}

export default ImportProspectsPopup
